import { livekitWSUrl } from "config/constant";
import { StreamingProvider } from "globals/enums";
import { RecordingService, RecordingServiceOptions } from "globals/interfaces";
import { handleError } from "helpers/sentry";
import {
  AudioPresets,
  ConnectionQuality,
  DisconnectReason,
  LocalAudioTrack,
  LocalTrackPublication,
  LocalVideoTrack,
  Room,
  RoomEvent,
  Track,
  VideoPresets
} from "livekit-client";

export class LivekitService implements RecordingService {
  public livekitRoom;
  private options: RecordingServiceOptions = {};

  async connect(options: RecordingServiceOptions) {
    this.options = options;
    try {
      const room = new Room({
        disconnectOnPageLeave: false,
        publishDefaults: {
          videoCodec: "vp8",
          backupCodec: {
            codec: "h264"
          },
          videoSimulcastLayers: [VideoPresets.h360, VideoPresets.h540]
        },
        videoCaptureDefaults: {
          resolution: VideoPresets.h540.resolution
        },
        stopLocalTrackOnUnpublish: false
      });
      await room.connect(livekitWSUrl, options.streamingToken, {
        autoSubscribe: false
      });

      room.on(RoomEvent.Disconnected, (reason) => {
        handleError(`Livekit disconnected: ${DisconnectReason[reason]}`);
        this.options.onDisconnect();
      });

      room.on(RoomEvent.Reconnecting, () => {
        this.options.onReconnecting();
      });

      room.on(RoomEvent.Reconnected, () => {
        this.options.onReconnected();
      });

      room.on(RoomEvent.LocalTrackPublished, (publish) => {
        if (
          room.localParticipant?.trackPublications.size >= 1 &&
          publish.track.source != Track.Source.ScreenShare
        ) {
          const audioTrackSid =
            room.localParticipant?.audioTrackPublications.entries().next().value?.[0] ?? null;
          const videoTrackSid =
            room.localParticipant?.videoTrackPublications.entries().next().value?.[0] ?? null;

          if (audioTrackSid || videoTrackSid) {
            const tracks: { videoTrackSid?: string; audioTrackSid?: string } = {
              ...(videoTrackSid && { videoTrackSid }),
              ...(audioTrackSid && { audioTrackSid })
            };
            this.options.onAudioVideoTrackPublished(
              {
                participantSid: room.localParticipant?.sid,
                tracks
              },
              StreamingProvider.Livekit
            );
          }
        }
      });

      room.on(RoomEvent.ConnectionQualityChanged, () => {
        const isGoodQuality = this.handleNetworkQualityChange(room);
        options.onNetworkQualityChange(isGoodQuality);
      });

      options.trackPublished(room);
      this.livekitRoom = room;
      console.log("connected", room.localParticipant);

      if (!options.tracks) {
        return;
      }
      const newVideoTrack = options.tracks.find((track) => track.kind === "video");
      const newAudioTrack = options.tracks.find((track) => track.kind === "audio");

      const clonedVideoTrack = newVideoTrack ? newVideoTrack.clone() : null;
      const clonedAudioTrack = newAudioTrack ? newAudioTrack.clone() : null;

      if (clonedVideoTrack) {
        await room.localParticipant.publishTrack(this.createLocalVideoTrack(clonedVideoTrack), {
          name: "camera",
          source: Track.Source.Camera
        });
      }

      if (clonedAudioTrack) {
        await room.localParticipant.publishTrack(new LocalAudioTrack(clonedAudioTrack), {
          name: "audio",
          source: Track.Source.Microphone,
          audioPreset: AudioPresets.speech
        });
      }
    } catch (e: any) {
      handleError("Livekit service connection failed");
      handleError(e);
      options.onConnectionFailure(e);
    }
  }

  handleNetworkQualityChange = (room) => {
    return (
      room.localParticipant.connectionQuality === ConnectionQuality.Good ||
      room.localParticipant.connectionQuality === ConnectionQuality.Excellent
    );
  };

  createLocalVideoTrack(track: MediaStreamTrack) {
    if (track) {
      return new LocalVideoTrack(track);
    }
  }

  async unpublishTrack(publishedTrack: LocalTrackPublication) {
    if (!publishedTrack || !this.livekitRoom) return;
    const track = publishedTrack.track;
    if (track) {
      track.stop();
      await this.livekitRoom.localParticipant.unpublishTrack(track);
    }
  }

  async flipVideo(tracks: MediaStreamTrack[]) {
    if (!tracks?.length) {
      return;
    }

    try {
      if (this.options?.tracks?.length >= 2) {
        await this.livekitRoom.localParticipant.videoTrackPublications.forEach((publication) => {
          if (publication.track && publication.source !== Track.Source.ScreenShare) {
            publication.track.stop();
            this.livekitRoom.localParticipant.unpublishTrack(publication.track);
          }
        });

        await this.livekitRoom.localParticipant.audioTrackPublications.forEach((publication) => {
          if (publication.track) {
            publication.track.stop();
            this.livekitRoom.localParticipant.unpublishTrack(publication.track);
          }
        });
      }
      const newVideoTrack = tracks.find((track) => track.kind === "video");
      if (!newVideoTrack) {
        throw new Error("No video track found");
      }
      const clonedVideoTrack = newVideoTrack.clone();
      await this.livekitRoom.localParticipant.publishTrack(
        this.createLocalVideoTrack(clonedVideoTrack),
        {
          name: "camera",
          source: Track.Source.Camera
        }
      );

      const newAudioTrack = tracks.find((track) => track.kind === "audio");

      if (!newAudioTrack && this.options.isAudio) {
        throw new Error("No audio track found");
      }

      if (this.options.isAudio && newAudioTrack) {
        const clonedAudioTrack = newAudioTrack.clone();
        await this.livekitRoom.localParticipant.publishTrack(
          new LocalAudioTrack(clonedAudioTrack),
          {
            name: "audio",
            source: Track.Source.Microphone,
            audioPreset: AudioPresets.speech
          }
        );
      }

      this.options.tracks = this.options.isAudio && newAudioTrack
        ? [newVideoTrack, newAudioTrack]
        : [newVideoTrack];
    } catch (e) {
      this.options.onConnectionFailure(e);
    }
  }

  disconnect(): void {
    console.log("Livekit Disconnected");
    if (this.livekitRoom) {
      try {
        this.livekitRoom.localParticipant?.trackPublications?.forEach(
          (publication: LocalTrackPublication) => {
            const track = publication.track;
            if (track) {
              track.stop();
              this.livekitRoom.localParticipant?.unpublishTrack(track);
            }
          }
        );
      } catch (error) {
        console.error("Failed to cleanup Livekit tracks", error);
      }
      this.livekitRoom.disconnect();
      this.livekitRoom = null;
    }
  }

  async publishTrack(track: LocalVideoTrack, trackName: string) {
    if (track) {
      const toPublishTrack = track;
      toPublishTrack.source = Track.Source.ScreenShare;

      try {
        const publishedTrack = await this.livekitRoom.localParticipant.publishTrack(
          toPublishTrack,
          { name: trackName }
        );
        return publishedTrack.track;
      } catch (e) {
        this.options.onConnectionFailure(e);
        return null;
      }
    }
  }
}
